import { AssetSearchTask } from '../../../types';

export interface SearchSelectModalState {
  elementType: string;
  elementName: string;
  state: string;
  currentRefCount: number;
  maxTotal: number;
}

export interface SearchSelectGroup {
  id: number;
  keywords: string;
  state: string;
  images: string[];
  status: string;
  taskId: string;
  retryCount: number;
}

export interface ActiveSearchDetail {
  category: number;
  categoryName: string;
  assetName: string;
  assetState: string;
  status: string;
  count: number;
}

export function getSearchSelectGroups(
  searchTasks: AssetSearchTask[],
  searchSelectModal: SearchSelectModalState | null,
) {
  if (!searchSelectModal) return [];
  const categoryMap: Record<string, number> = { character: 1, scene: 2, prop: 3 };
  const category = categoryMap[searchSelectModal.elementType] || 0;
  const isBase = searchSelectModal.state === '__base__';
  const matched = searchTasks.filter(task =>
    task.category === category &&
    task.assetName === searchSelectModal.elementName &&
    (isBase ? true : task.assetState === (searchSelectModal.state || ''))
  );
  return matched
    .sort((a, b) => {
      if (isBase && a.assetState !== b.assetState) return a.assetState.localeCompare(b.assetState);
      return a.keywordIndex - b.keywordIndex;
    })
    .map(task => ({
      id: task.id,
      keywords: task.keywords,
      state: task.assetState || '',
      images: task.parsedImageUrls || [],
      status: task.status,
      taskId: task.taskId || '',
      retryCount: task.retryCount || 0,
    }));
}

export function getSearchCounts(searchTasks: AssetSearchTask[]) {
  const searchingCount = searchTasks.filter(task => task.status === 'queued' || task.status === 'searching').length;
  const searchQueuedCount = searchTasks.filter(task => task.status === 'queued').length;
  const searchActiveCount = searchTasks.filter(task => task.status === 'searching').length;
  const isGeneratingKeywords = searchTasks.some(task => task.status === 'generating_keywords');
  return {
    searchingCount,
    searchQueuedCount,
    searchActiveCount,
    isGeneratingKeywords,
    searchIsActive: searchingCount > 0 || isGeneratingKeywords,
  };
}

export function getActiveSearchDetails(
  searchTasks: AssetSearchTask[],
  categoryLabel: Record<number, string>,
): ActiveSearchDetail[] {
  const active = searchTasks.filter(task => task.status === 'queued' || task.status === 'searching');
  const seen = new Map<string, ActiveSearchDetail>();
  for (const task of active) {
    const key = `${task.category}:${task.assetName}:${task.assetState}`;
    const existing = seen.get(key);
    if (existing) {
      existing.count++;
      if (task.status === 'searching') existing.status = 'searching';
    } else {
      seen.set(key, {
        category: task.category,
        categoryName: categoryLabel[task.category] || '',
        assetName: task.assetName,
        assetState: task.assetState,
        status: task.status,
        count: 1,
      });
    }
  }
  return Array.from(seen.values()).sort((a, b) => a.category - b.category || a.assetName.localeCompare(b.assetName));
}

export function getSearchSummary(searchTasks: AssetSearchTask[]) {
  const realTasks = searchTasks.filter(task => task.category !== 0);
  if (realTasks.length === 0) return null;
  const completedOrFailed = realTasks.every(task => task.status === 'completed' || task.status === 'failed');
  if (!completedOrFailed) return null;
  const assetNames = new Set(realTasks.map(task => `${task.category}:${task.assetName}:${task.assetState}`));
  const latestTime = realTasks.reduce((max, task) => task.updatedAt > max ? task.updatedAt : max, '');
  return { assetCount: assetNames.size, time: latestTime };
}

export function isKeywordPlaceholderTimedOut(searchTasks: AssetSearchTask[], isGeneratingKeywords: boolean) {
  if (!isGeneratingKeywords) return false;
  const placeholder = searchTasks.find(task => task.status === 'generating_keywords');
  if (!placeholder) return false;
  return Date.now() - new Date(placeholder.createdAt).getTime() > 5 * 60 * 1000;
}
